import { useHistory } from 'react-router-dom';
import { Redirect, useLocation } from 'react-router';
import { useEffect, useState, ReactNode } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { Button } from '../../components/Button/Button';
import { H1 } from '../../components/H1/H1';
import { HOME_URL, API_URL, PAYMENT_FLOW_V1_ENABLED } from '../../config';
import { Link } from '../../components/Link/Link';
import { OrderSummary } from '../../components/OrderSummary/OrderSummary';
import { Text } from '../../components/Text/Text';
import styles from './OrderComplete.module.scss';
import { useStore } from '../../store';
import { CUSTOMER_PORTAL_URL } from '../../config';
import Cookies from 'js-cookie';

type BookingStatus = 'idle' | 'loading' | 'booked' | 'rejected' | 'timeout';

const BOOKING_POPOVER = {
  label: 'Courier booking in process, please wait for label to be generated.',
  invoice: 'Courier booking in process, please wait for invoice to be generated.',
} as const;

function BookingButtonWrap({
  showPopover,
  variant,
  children,
}: {
  showPopover: boolean;
  variant: 'label' | 'invoice';
  children: ReactNode;
}) {
  const message = BOOKING_POPOVER[variant];
  const wrapper = <span className={styles.buttonWrapper}>{children}</span>;
  if (!showPopover) return wrapper;
  return (
    <OverlayTrigger
      trigger="hover"
      placement="top"
      delay={{ show: 200, hide: 100 }}
      overlay={
        <Popover id={`booking-popover-${variant}`} className={styles.bookingPopover}>
          <Popover.Body className={styles.bookingPopoverBody}>{message}</Popover.Body>
        </Popover>
      }
    >
      {wrapper}
    </OverlayTrigger>
  );
}

function BookingDownloadButtons({
  labelText,
  sseLabelUrl,
  sseInvoiceUrl,
  bookingStatus,
}: {
  labelText: 'Download Label' | 'Download Labels';
  sseLabelUrl: string | null;
  sseInvoiceUrl: string | null;
  bookingStatus: BookingStatus;
}) {
  const isLoading = bookingStatus === 'loading';
  return (
    <>
      <div className={styles.buttonBar}>
        <BookingButtonWrap showPopover={isLoading} variant="label">
          <Link href={sseLabelUrl || '#'} className={!sseLabelUrl ? styles.linkDisabled : undefined}>
            <Button disabled={!sseLabelUrl} loading={isLoading}>{labelText}</Button>
          </Link>
        </BookingButtonWrap>
      </div>
      <div className={styles.buttonBar}>
        <BookingButtonWrap showPopover={isLoading} variant="invoice">
          <Link href={sseInvoiceUrl || '#'} className={!sseInvoiceUrl ? styles.linkDisabled : undefined}>
            <Button disabled={!sseInvoiceUrl} loading={isLoading}>Download Invoice</Button>
          </Link>
        </BookingButtonWrap>
      </div>
    </>
  );
}

export function OrderComplete() {
  const m = useLocation();
  const history = useHistory();
  const orderId = new URLSearchParams(m.search).get('ref');
  const newUser = new URLSearchParams(m.search).get('new');
  const label = new URLSearchParams(m.search).get('label');
  const invoice = new URLSearchParams(m.search).get('invoice');
  const payment_type = new URLSearchParams(m.search).get('payment_type');
  const isAsyncProcessing = new URLSearchParams(m.search).get('async') === '1';

  const [bookingStatus, setBookingStatus] = useState<BookingStatus>('idle');
  const [sseLabelUrl, setSseLabelUrl] = useState<string | null>(null);
  const [sseInvoiceUrl, setSseInvoiceUrl] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const customOrder = useStore((state) => state.customOrder);
  const setOrderId = useStore((state) => state.setOrderId);
  const setOrderRefId = useStore((state) => state.setOrderRefId);
  const setSendSamePackage = useStore((state) => state.setSendSamePackage);
  let newUserData = null;

  const oldUser = Cookies.get('loggedin_user');//get user email

  console.log(oldUser);

  if (oldUser) {
    if (typeof oldUser === 'undefined') {
      if (newUser) {
        try {
          newUserData = JSON.parse(atob(newUser));
        } catch (e) { }
      }
    }
  } else {
    if (newUser) {
      try {
        newUserData = JSON.parse(atob(newUser));
      } catch (e) { }
    }
  }

  // SSE: stream order status when async payment (V1) so we can enable Label/Invoice when booking completes
  useEffect(() => {
    if (!PAYMENT_FLOW_V1_ENABLED || !orderId || typeof EventSource === 'undefined') {
      setBookingStatus('idle');
      return;
    }
    setBookingStatus('loading');
    setBookingError(null);
    const streamUrl = `${API_URL}/orders/${orderId}/stream`;
    const es = new EventSource(streamUrl);
    let closed = false;
    const closeStream = () => {
      if (closed) return;
      closed = true;
      try { es.close(); } catch (e) {}
    };
    const onStatus = (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data) as { order_status: string | null; label_url: string | null; invoice_url: string | null };
        closeStream();
        setSseLabelUrl(payload.label_url || null);
        setSseInvoiceUrl(payload.invoice_url || null);

        if (payload.order_status === 'order_rejected') {
          setBookingError('We could not book your order with the courier. Please contact support and quote your reference number.');
          setBookingStatus('rejected');
        } else {
          setBookingStatus('booked');
        }
      } catch {
        closeStream();
      }
    };
    es.addEventListener('status', onStatus);
    es.onerror = () => {
      closeStream();
      setBookingError('Booking is taking longer than expected. Please try again later or contact support.');
      setBookingStatus('timeout');
    };
    return () => {
      closeStream();
    };
  }, [orderId]);

  if (!orderId) {
    return <Redirect to="/package-details" />;
  }
  setSendSamePackage(false);
  setOrderId('');
  setOrderRefId(orderId);

  const setSamePackage = () => {
    setSendSamePackage(true);
    history.push('/package-details');
  };
  return (
    <div className={styles.root}>
      <H1>Success!</H1>
      <OrderSummary
        className={styles.orderSummary}
        addressDetailsEditEnabled={false}
        quoteEditEnabled={false}
        packageDetailsEditEnabled={false}
        variant="payment-package-card"
      />
      {!customOrder && (
        <Text className={styles.text}>
          {PAYMENT_FLOW_V1_ENABLED ? (
            <>
              <p className={styles.messageLead}>Your payment has been processed successfully.</p>
              {bookingStatus === 'loading' && (
                <p className={styles.bookingMessage}>We are booking your order with the courier. You can wait here for your label and invoice.</p>
              )}
              {bookingError && (
                <div className={styles.bookingError}>{bookingError}</div>
              )}
              {bookingStatus === 'booked' && (
                <p>Your order is confirmed. We will email your label and tracking details shortly.</p>
              )}
              <p className={styles.referenceLine}><strong>Reference:</strong> <code>{orderId}</code></p>
              {PAYMENT_FLOW_V1_ENABLED && !newUserData && !oldUser && (
                <div className={styles.parentButtonDiv}>
                  <BookingDownloadButtons labelText="Download Label" sseLabelUrl={sseLabelUrl} sseInvoiceUrl={sseInvoiceUrl} bookingStatus={bookingStatus} />
                  <div className={styles.buttonBar}>
                    <Button className={styles.samePackageButton} onClick={setSamePackage}>Send the same package</Button>
                  </div>
                </div>
              )}
              {!newUserData && !oldUser && (
                <>
                  <p className={styles.messageSub}>Create an account to track this and future orders.</p>
                  <div className={styles.parentButtonDiv}>
                    <div className={styles.buttonBar}>
                      <Link href={CUSTOMER_PORTAL_URL + "/sign-up?refId=" + orderId}><Button>Quick register</Button></Link>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <p className={styles.messageLead}>Your order has been submitted. You will receive an email shortly.</p>
              <p className={styles.messageSub}>Check your spam folder if you do not see it.</p>
              <p className={styles.referenceLine}><strong>Reference:</strong> <code>{orderId}</code></p>
              {!newUserData && !oldUser && (
                <div className={styles.parentButtonDiv}>
                  <div className={styles.buttonBar}>
                    <Button onClick={setSamePackage}>Send the same package</Button>
                  </div>
                  {label && (
                    <div className={styles.buttonBar}>
                      <Link className={styles.samePackageButton} href={label || ''}><Button>Download Labels</Button></Link>
                    </div>
                  )}
                  <div className={styles.buttonBar}>
                    <Link href={invoice || ''}><Button>Download Invoice</Button></Link>
                  </div>
                </div>
              )}
            </>
          )}
        </Text>
      )}

      {customOrder && (
        <Text className={styles.text}>
          {PAYMENT_FLOW_V1_ENABLED && (
            <>
              {bookingStatus === 'loading' && (
                <p className={styles.bookingMessage}>We are booking your order with the courier. You can wait here for your label and invoice.</p>
              )}
              {bookingError && (
                <div className={styles.bookingError}>{bookingError}</div>
              )}
              {bookingStatus === 'booked' && (
                <p>Your order is confirmed. We will email your label and tracking details shortly.</p>
              )}
            </>
          )}
          <p className={styles.messageLead}>Your custom order has been received. We will be in touch shortly.</p>
          <p className={styles.referenceLine}><strong>Reference:</strong> <code>{orderId}</code></p>
          {!newUserData && (
            <div className={styles.parentButtonDiv}>
              <div className={styles.buttonBar}>
                <Button onClick={setSamePackage}>Send the same package</Button>
              </div>
              {PAYMENT_FLOW_V1_ENABLED ? (
                <BookingDownloadButtons labelText="Download Labels" sseLabelUrl={sseLabelUrl} sseInvoiceUrl={sseInvoiceUrl} bookingStatus={bookingStatus} />
              ) : (
                <>
                  {label && (
                    <div className={styles.buttonBar}>
                      <Link className={styles.samePackageButton} href={label || ''}><Button>Download Labels</Button></Link>
                    </div>
                  )}
                  <div className={styles.buttonBar}>
                    <Link href={invoice || ''}><Button>Download Invoice</Button></Link>
                  </div>
                </>
              )}
            </div>
          )}
        </Text>
      )}

      {oldUser && (
        <Text className={styles.text}>
          {payment_type !== '2' && (
            <p className={styles.messageLead}>You are logged in as {oldUser}. Link this order to your account to see it in your dashboard.</p>
          )}
          <div className={styles.parentButtonDiv}>
            {payment_type !== '2' && (
              <div className={styles.buttonBar}>
                <Link href={CUSTOMER_PORTAL_URL + "/bind?refId=" + orderId + "&email=" + encodeURIComponent(oldUser)}><Button>Assign order to my account</Button></Link>
              </div>
            )}
            <div className={styles.buttonBar}>
              <Button className={styles.samePackageButton} onClick={setSamePackage}>Send the same package</Button>
            </div>
            {PAYMENT_FLOW_V1_ENABLED ? (
              <BookingDownloadButtons labelText="Download Labels" sseLabelUrl={sseLabelUrl} sseInvoiceUrl={sseInvoiceUrl} bookingStatus={bookingStatus} />
            ) : (
              <>
                {label && (
                  <div className={styles.buttonBar}>
                    <Link href={label || ''}><Button>Download Labels</Button></Link>
                  </div>
                )}
                <div className={styles.buttonBar}>
                  <Link className={label ? styles.samePackageButton : ''} href={invoice || ''}><Button>Download Invoice</Button></Link>
                </div>
              </>
            )}
          </div>
        </Text>
      )}

      {newUserData && (
        <Text className={styles.text}>
          <p className={styles.messageLead}>Welcome, {newUserData.firstName}! This is your first order with us.</p>
          <p className={styles.messageSub}>Create an account to track this and future orders in one place.</p>
          <div className={styles.parentButtonDiv}>
            <div className={styles.buttonBar}>
              <Link href={CUSTOMER_PORTAL_URL + "/sign-up?refId=" + orderId}><Button>Quick register</Button></Link>
            </div>
            <div className={styles.buttonBar}>
              <Button className={styles.samePackageButton} onClick={setSamePackage}>Send the same package</Button>
            </div>
            {PAYMENT_FLOW_V1_ENABLED ? (
              <BookingDownloadButtons labelText="Download Labels" sseLabelUrl={sseLabelUrl} sseInvoiceUrl={sseInvoiceUrl} bookingStatus={bookingStatus} />
            ) : (
              <>
                {label && (
                  <div className={styles.buttonBar}>
                    <Link href={label || ''}><Button>Download Labels</Button></Link>
                  </div>
                )}
                <div className={styles.buttonBar}>
                  <Link className={label ? styles.samePackageButton : ''} href={invoice || ''}><Button>Download Invoice</Button></Link>
                </div>
              </>
            )}
          </div>
        </Text>
      )}
      <div className={styles.toolbar}>
        <Link href={HOME_URL}>Return Home</Link>
      </div>
    </div>
  );
}
